<template>
  <div
    class="row-table w-full justify-center h-[72px] md:h-[105px] overflow-hidden"
    :style="`background: ${colorDefination.bg};`"
  >
    <div :class="`${colorDefination.stripe} w-2 md:w-3`" :style="colorDefination.stripeStyle"></div>
    <div class="flex flex-col p-1.5 md:p-1.5 flex-grow min-w-0">
      <div class="flex justify-start items-center gap-x-1 md:gap-x-1.5">
        <v-avatar
          v-if="stationIsAlert"
          color="white"
          size="21"
          class="shrink-0"
          :class="{ 'mobile-avatar': !mobile }"
        >
          <span class="text-[15px] font-bold text-negative">!</span>
        </v-avatar>
        <v-avatar
          v-else
          color="primary"
          size="21"
          class="shrink-0"
          :class="{ 'mobile-avatar': !mobile }"
          :style="npsRingStyle"
        >
          <ProtectedAvatar :src="resolvedPhoto" :size="21">
            <span class="font-bold" :class="mobile ? 'text-[10px]' : 'text-[.6rem]'">{{
              avatarInitials
            }}</span>
          </ProtectedAvatar>
        </v-avatar>
        <div class="min-w-0 flex-grow">
          <span
            v-if="!ourTeam"
            class="flex items-center justify-center gap-[10px] rounded-2xl mb-[3px] px-2 py-0 font-sans font-normal not-italic leading-[14px] tracking-[0.06px] text-[var(--Tertiary-40)]"
            :class="[
              defineOurTeamBgColor,
              mobile
                ? 'h-[18px] w-fit text-[10px]'
                : 'h-[16px] md:h-[21px] w-[40px] md:w-[53px] text-[8px] md:text-[10px]'
            ]"
            >Externo</span
          >
          <v-tooltip :text="name" location="top">
            <template v-slot:activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="two-line-ellipsis text-left text-gray-800 leading-tight cursor-default"
                :class="[
                  isAlertTextColor,
                  mobile ? 'text-[11px]' : 'text-[9px] md:text-[12px] md:max-w-[100px]'
                ]"
              >
                {{ name }}
              </span>
            </template>
          </v-tooltip>
        </div>
      </div>
      <div class="flex justify-start gap-x-1 md:gap-x-1.5 -mt-0.5 md:-mt-1">
        <div class="w-[14px] md:w-[20px] h-[8px] md:h-[20px] shrink-0"></div>
        <p
          class="div3 text-left"
          :class="[
            stationIsAlert ? 'text-white' : 'text-gray-700',
            mobile ? 'text-[10px]' : 'text-[8px] md:text-[10px]'
          ]"
        >
          {{ crm }}
        </p>
      </div>
      <div class="mt-auto w-full">
        <template v-if="stationIsAlert">
          <div class="w-full h-[14px] md:h-[18px] rounded-sm bg-white/30"></div>
        </template>
        <template v-else>
          <div class="flex justify-start gap-x-1 md:gap-x-1.5">
            <div
              class="group-buttons w-full flex items-center justify-center gap-1 md:gap-2 flex-wrap"
            >
              <v-tooltip v-if="hasObservations" text="Existe observações">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" color="warning" v-bind="props" :icon="mdiTextBoxCheck" />
                </template>
              </v-tooltip>
              <v-tooltip v-if="doctorPending" text="Médico pendente">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" color="primary" v-bind="props" :icon="mdiAccountAlert" />
                </template>
              </v-tooltip>
              <v-tooltip v-if="!doctorConfirmed" text="Plantão pendente">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    color="neutral"
                    v-bind="props"
                    :icon="mdiClockAlertOutline"
                  />
                </template>
              </v-tooltip>
              <v-tooltip v-if="doctorConfirmed" text="Plantão confirmado">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    color="success"
                    v-bind="props"
                    :icon="mdiClockCheckOutline"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="doctorStationStatus !== DoctorStationStatus.SEM_STATUS && !notShowStatusIcon"
                :text="doctorStationStatusTranslate[doctorStationStatus].text"
              >
                <template v-slot:activator="{ props }">
                  <component
                    v-bind="props"
                    :is="doctorStationStatusTranslate[doctorStationStatus].icon"
                  />
                </template>
              </v-tooltip>

              <v-tooltip v-if="exceptionSummaryStatus" :text="exceptionTooltipText">
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    size="small"
                    :icon="exceptionIconPath"
                    :color="exceptionIconColor"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="flags.enabledPostStation && postStationStatus"
                :text="postStationTooltipText"
              >
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    data-testid="post-station-status-icon"
                    size="small"
                    :icon="mdiClockEnd"
                    :color="postStationIconColor"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="flags.enabledPermanenceRecord && permanenceTooltip"
                :text="permanenceTooltip"
              >
                <template v-slot:activator="{ props: tooltipProps }">
                  <PermanenceRecordIcon
                    v-bind="tooltipProps"
                    data-testid="permanence-record-icon"
                  />
                </template>
              </v-tooltip>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getInitialsChar } from '@/utils/string'
import { nameInitials, stripHonorific } from '@/utils/stationDisplay'
import { resolveAvatarSrc } from '@/utils/avatar'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import PermanenceRecordIcon from '@/assets/icons/checks/permanence_registro.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  mdiTextBoxCheck,
  mdiAccountAlert,
  mdiClockAlertOutline,
  mdiClockCheckOutline,
  mdiClockEnd
} from '@mdi/js'
import {
  DoctorStationStatus,
  doctorStationStatusTranslate
} from '@/translations/doctor.station.status'
import {
  DoctorStationReminderStatus,
  ExceptionSummaryStatus
} from '@/Network/Types/Responses/StationResponses'
import type { PostStationCellStatus } from '@/Network/Types/Responses/StationResponses'
import { useStationRowStyle, type StationRowCellType } from './useStationRowStyle'

export type CellTypes = StationRowCellType

const props = withDefaults(
  defineProps<{
    tableType?: CellTypes
    name?: string
    text?: string
    minimal?: boolean
    flat?: boolean
    solid?: boolean
    crm?: string
    success?: boolean
    error?: boolean
    waiting?: boolean
    pendences?: boolean
    photo?: string
    hasObservations?: boolean
    doctorPending?: boolean
    doctorConfirmed?: boolean
    doctorStationStatus?: DoctorStationStatus
    ourTeam?: boolean
    doctorStationReminderStatus?: DoctorStationReminderStatus
    isAlert?: boolean
    requiredCheckNotOurTeam?: boolean
    requiredCheckOurTeam?: boolean
    npsScore?: number | null
    exceptionSummaryStatus?: ExceptionSummaryStatus | null
    postStationStatus?: PostStationCellStatus | null
    permanenceTooltip?: string | null
    colorOverride?: { stripe: string; bg: string } | null
    /** Grade do celular (R1b, `isMobileLayout`): texto ≥ 10px em vez do encolhimento do `max-md`. */
    mobile?: boolean
  }>(),
  {
    tableType: 'EXTRA',
    name: '',
    text: '',
    minimal: false,
    flat: false,
    solid: false,
    crm: '',
    success: true,
    error: true,
    waiting: true,
    pendences: true,
    photo: '',
    hasObservations: false,
    doctorPending: false,
    doctorConfirmed: false,
    doctorStationStatus: DoctorStationStatus.SEM_STATUS,
    ourTeam: false,
    doctorStationReminderStatus: DoctorStationReminderStatus.PENDING,
    npsScore: null,
    isAlert: false,
    exceptionSummaryStatus: null,
    postStationStatus: null,
    permanenceTooltip: null,
    colorOverride: null,
    mobile: false
  }
)

const { flags } = useFeatureFlags()

const resolvedPhoto = computed(() => resolveAvatarSrc(props.photo))
/** QA-11: no celular as iniciais saem do nome sem "Dr./Dra." (AS); o desktop segue igual ao main. */
const avatarInitials = computed(() =>
  props.mobile ? nameInitials(stripHonorific(props.name)) : getInitialsChar(props.name)
)

// Cor, alerta e ícones vêm do composable (reusado pelo StationListCard do celular).
const {
  stationIsAlert,
  npsRingStyle,
  colorDefination,
  defineOurTeamBgColor,
  isAlertTextColor,
  notShowStatusIcon,
  exceptionIconPath,
  exceptionIconColor,
  exceptionTooltipText,
  postStationIconColor,
  postStationTooltipText
} = useStationRowStyle(() => ({
  tableType: props.tableType,
  solid: props.solid,
  ourTeam: props.ourTeam,
  isAlert: props.isAlert,
  colorOverride: props.colorOverride,
  requiredCheckOurTeam: props.requiredCheckOurTeam,
  requiredCheckNotOurTeam: props.requiredCheckNotOurTeam,
  exceptionSummaryStatus: props.exceptionSummaryStatus,
  postStationStatus: props.postStationStatus,
  npsScore: props.npsScore,
  hasObservations: props.hasObservations,
  doctorPending: props.doctorPending,
  doctorConfirmed: props.doctorConfirmed,
  doctorStationStatus: props.doctorStationStatus,
  permanenceTooltip: props.permanenceTooltip
}))
</script>

<style scoped>
.row-table {
  display: flex;
}

.decoration-left {
  width: 18px !important;
}

.btn-green,
.btn-red,
.btn-yellow {
  width: 14px;
  height: 14px;
}

.btn-green {
  border: 1px #198038 solid;
}

.btn-red {
  border: 1px theme('colors.primary10') solid;
}

.btn-yellow {
  border: 1px #f1c21b solid;
}

.group-buttons {
  gap: 1px;
}

.two-line-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}

.rejected-symbols-container {
  background-color: #fce9ea;
  border-radius: 6px;
}

@media screen and (max-width: 767px) {
  .two-line-ellipsis {
    -webkit-line-clamp: 2;
    line-clamp: 2;
    max-width: none;
  }

  .mobile-avatar {
    width: 14px !important;
    height: 14px !important;
    min-width: 14px !important;
  }

  .mobile-avatar span {
    font-size: 7px !important;
  }

  .group-buttons .v-icon {
    font-size: 12px !important;
  }

  .group-buttons :deep(svg) {
    width: 12px !important;
    height: 12px !important;
  }
}
</style>
